import { get, post, put, del } from '@/lib/api/client'
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  Video,
  CreateVideo,
  UpdateVideo,
  UpdateVideoStatus,
  VideoFilterParams,
  VideoFilterResponse,
  VideoStats,
  VideoDashboardStats
} from '@/lib/api/types'

export const videoService = {

  createVideo: async (data: CreateVideo): Promise<ApiResponse<Video>> => {
    return await post<ApiResponse<Video>>(
      'videos/admin/private',
      data
    )
  },


  updateVideo: async (
    videoId: string,
    data: UpdateVideo
  ): Promise<ApiResponse<Video>> => {
    return await put<ApiResponse<Video>>(
      `videos/admin/private/${videoId}`,
      data
    )
  },


  deleteVideo: async (videoId: string): Promise<ApiResponse<null>> => {
    return await del<ApiResponse<null>>(
      `videos/admin/private/${videoId}`
    )
  },


  updateVideoStatus: async (
    videoId: string,
    data: UpdateVideoStatus
  ): Promise<ApiResponse<null>> => {
    return await put<ApiResponse<null>>(
      `videos/admin/private/${videoId}/status`,
      data
    )
  },


  filterVideos: async (
    params: PaginationParams = { page: 1, size: 10 },
    filters: VideoFilterParams = {}
  ): Promise<ApiResponse<VideoFilterResponse>> => {
    return await post<ApiResponse<VideoFilterResponse>>(
      `videos/private/filter?size=${params.size}&page=${params.page}`,
      filters
    )
  },


  getVideoById: async (videoId: string): Promise<ApiResponse<Video>> => {
    return await get<ApiResponse<Video>>(
      `videos/admin/private/${videoId}`
    )
  },


  getTotalStats: async (): Promise<ApiResponse<number>> => {
    return await get<ApiResponse<number>>(
      'videos/private/stats/total'
    )
  },


  getPublishedStats: async (): Promise<ApiResponse<number>> => {
    return await get<ApiResponse<number>>(
      'videos/private/stats/published'
    )
  },


  getDraftStats: async (): Promise<ApiResponse<number>> => {
    return await get<ApiResponse<number>>(
      'videos/private/stats/draft'
    )
  },

  // Get all video stats (combined)
  getVideoStats: async (): Promise<ApiResponse<VideoStats>> => {
    const [totalResponse, publishedResponse, draftResponse] = await Promise.all([
      videoService.getTotalStats(),
      videoService.getPublishedStats(),
      videoService.getDraftStats()
    ])

    const stats: VideoStats = {
      total: totalResponse.data || 0,
      published: publishedResponse.data || 0,
      draft: draftResponse.data || 0
    }

    return {
      success: true,
      message: 'Video stats retrieved successfully',
      data: stats
    }
  },

  // Search videos
  searchVideos: async (
    params: PaginationParams = { page: 1, size: 10 },
    searchBy?: string
  ): Promise<ApiResponse<PaginatedResponse<Video>>> => {
    const queryParams = new URLSearchParams({
      page: params.page.toString(),
      size: params.size.toString()
    })

    if (searchBy !== undefined) {
      queryParams.append('searchBy', searchBy)
    }

    return await get<ApiResponse<PaginatedResponse<Video>>>(
      `videos/private/search?${queryParams.toString()}`
    )
  },

  // Get Home Page Highlights
  getHomePageHighlights: async (type: string = 'HOME_PAGE_IS_KEY_HIGHLIGHT'): Promise<ApiResponse<Video[]>> => {
    return await get<ApiResponse<Video[]>>(
      `videos/public/highlights?type=${type}`
    )
  },

  // Update Home Page Highlights
  updateHomePageHighlights: async (videoUploadIds: string[], type: string = 'HOME_PAGE_IS_KEY_HIGHLIGHT'): Promise<ApiResponse<Video[]>> => {
    return await post<ApiResponse<Video[]>>(
      'videos/private/highlight',
      {
        videoUploadIds,
        highlightType: type
      }
    )
  },

  // Get video dashboard stats
  getVideoDashboardStats: async (): Promise<ApiResponse<VideoDashboardStats>> => {
    return await get<ApiResponse<VideoDashboardStats>>(
      'videos/private/stats'
    )
  }
}
